import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { startWith } from 'rxjs';

import { FileService } from '@blaze-runtime/engine-feature';
import { BlazeAccountFacade } from '@blaze-shared/app-state-data-access';
import { urlUtils } from '@blaze-shared/platform-util';
import { ButtonIconComponent, safeDetectChanges, TooltipDirective } from '@blaze-shared/shared-ui';
import { filenameFromUrlRegex } from '@blaze-shared/shared-util';
import { getItemTypeIcon, UploaderFacade, UploaderOptions } from '@blaze-shared/uploader';

import { BaseCellInputComponent } from '../base-cell-input.component';

interface FileValue {
  title: string;
  imgIcon: string;
  fileUrl: string;
  href?: string;
}

@UntilDestroy()
@Component({
  selector: 'blaze-cell-input-file',
  templateUrl: `./cell-input-file.component.html`,
  styleUrls: ['./cell-input-file.component.scss', '../base-cell-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MatIcon, MatMenuModule, TooltipDirective, ButtonIconComponent],
})
export class CellInputFileComponent extends BaseCellInputComponent<string | string[]> {
  private readonly blazeAccountFacade = inject(BlazeAccountFacade);
  private readonly fileService = inject(FileService);
  private readonly uploader = inject(UploaderFacade);

  @Input() readonly = false;
  @Input() projectId: string;

  @ViewChild('addFileButton') addFileButtonRef: ElementRef<HTMLButtonElement>;
  @ViewChild('fileInput') fileInputRef: ElementRef<HTMLInputElement> | undefined;
  @ViewChildren('filesRefs') filesRefsQue?: QueryList<ElementRef<HTMLImageElement>>;

  draggingFileOver = false;
  filesFormControl = new FormControl<FileValue[] | null>(null);

  get acceptedFiletypes(): string {
    if (this.column.columnType === 'Image') {
      return this.uploader.supportedImageTypes.join(', ');
    }

    return '';
  }

  get isImageColumn(): boolean {
    return this.column?.columnType === 'Image';
  }

  get fileInput(): HTMLInputElement | undefined {
    return this.fileInputRef?.nativeElement;
  }

  downloadFile(event: MouseEvent, fileUrl: string, isSecure = false): void {
    event.preventDefault();
    if (!isSecure) {
      this.fileService.downloadFile(fileUrl);
      return;
    }

    this.fileService.downloadSecureFile(fileUrl);
  }

  fileRemove(index: number): void {
    const oldValue = Array.isArray(this.formControl.value)
      ? this.formControl.value
      : [this.formControl.value];
    const value = oldValue.filter((_, idx) => idx !== index);
    this.value = value;

    const newVal = this.column?.multipleValues ? value : (value?.[0] ?? null);
    this.emitValueChanged(newVal);
    this.formControl.patchValue(newVal);
  }

  fileSelected(fileInput: EventTarget | null): void {
    const fileInputElement = fileInput as HTMLInputElement;
    const preselectFiles = Array.from(fileInputElement?.files ?? []);
    fileInputElement.value = '';
    if (!preselectFiles || preselectFiles.length === 0) {
      return;
    }

    this.fileUpload(preselectFiles);
  }

  fileDrop(event: DragEvent): void {
    event.stopPropagation();
    event.preventDefault();

    const preselectFiles = Array.from(event.dataTransfer?.files ?? []);
    if (!preselectFiles || preselectFiles.length === 0) {
      return;
    }

    this.draggingFileOver = false;

    this.fileUpload(preselectFiles);
  }

  fileAllowDrop(event: DragEvent): void {
    event.stopPropagation();
    event.preventDefault();

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }

    this.draggingFileOver = true;
  }

  override executeExtraStartAction(): void {
    if (this.extraStartAction?.type === 'file-add' || this.extraStartAction?.type === 'image-add') {
      afterNextRender(
        () => {
          this.fileInput?.click();
        },
        {
          injector: this.injector,
        },
      );
    } else if (
      this.extraStartAction?.type === 'file-clicked' ||
      this.extraStartAction?.type === 'image-clicked'
    ) {
      const fileClicked = this.extraStartAction.file;
      if (!fileClicked) {
        return;
      }
      afterNextRender(
        () => {
          const filesArray = this.filesRefsQue?.toArray();
          if (!filesArray) {
            return;
          }
          const file: HTMLImageElement | undefined = filesArray.find((el) =>
            el.nativeElement.alt.includes(fileClicked),
          )?.nativeElement;
          this.openMenuWhenImgIsReady(file);
        },
        {
          injector: this.injector,
        },
      );
    }
  }

  protected override createFormControl(): FormControl<string[] | null> {
    const value = (Array.isArray(this.value) ? this.value : [this.value]).filter(
      Boolean,
    ) as string[];
    const formControl = new FormControl({
      value: value,
      disabled: this.isDisabled,
    });
    formControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.emitValueChanged(value as string[] | null);
    });
    formControl.valueChanges.pipe(startWith(value), untilDestroyed(this)).subscribe((value) => {
      if (!value) {
        return this.filesFormControl.patchValue([]);
      }
      const newValues = Array.isArray(value) ? value : [value];
      this.filesFormControl.patchValue(
        newValues?.map((fileUrl) => ({
          title: this.getFileTitle(fileUrl),
          imgIcon: this.getFileTypeIcon(fileUrl),
          fileUrl: fileUrl,
          href: urlUtils.getFileUrlHref(fileUrl, this.column.isSecure),
        })) || [],
      );
    });
    return formControl;
  }

  private fileUpload(preselectFiles: File[]): void {
    const options: UploaderOptions = {
      accountId: this.blazeAccountFacade.getAccountId(),
      preselectFiles,
      acceptedFileTypes:
        this.column.columnType === 'Image' ? this.uploader.supportedImageTypes : ['*.*'],
      isMultipleSelection: this.column?.multipleValues || false,
    };

    // If upload is to secure vault - pass project id and the flag
    if (this.column.isSecure) {
      options.projectId = this.projectId;
      options.isSecure = true;
    }

    this.uploader.upload(options).subscribe((results) => {
      if (!results) {
        return;
      }

      const selected = this.formControl.value || [];
      selected.push(...results.map((file) => file.url as string));

      safeDetectChanges(this.cdr);

      const newVal = this.column?.multipleValues ? selected : (selected?.[0] ?? null);
      this.emitValueChanged(newVal);
      this.formControl.patchValue(newVal);
    });
  }

  private openMenuWhenImgIsReady(img: HTMLImageElement | undefined): void {
    if (!img) {
      return;
    } else if (img.complete) {
      img.click();
    } else {
      img.onload = (): void => {
        img.click();
        safeDetectChanges(this.cdr);
      };
    }
  }

  private getFileTitle(fileUrl: string): string {
    return fileUrl.match(filenameFromUrlRegex)?.[0] || fileUrl;
  }

  private getFileTypeIcon(fileName: string): string {
    if (this.column.columnType === 'Image') {
      return fileName;
    }

    return getItemTypeIcon(fileName);
  }
}
